import { useEffect } from 'react';
import moment from 'moment';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import download from 'src/utils/download';
import { getRequest, resolveQueryString } from 'src/utils/request';
import {
  ErrorType,
  useMessageHandler,
} from 'src/utils/hooks/useMessageHandler';
import messages from '../../messages';

const downloadCandidates = async (
  filters?: Array<{
    value?: Array<string> | string | null;
    name?: string | null;
    operator?: string | null;
    label?: string | null;
  }>,
) => {
  const query = filters ? resolveQueryString([filters]) : '';

  const { data } = await getRequest(`${API.GET.downloadCandidates(query)}`, {
    responseType: 'blob',
  });

  const now = moment().format(`${moment.HTML5_FMT.DATETIME_LOCAL}`);
  download(data, `Staff-${now}.csv`, 'text/csv');
  return data;
};

export const useDownloadCandidates = (
  filters?: Array<{
    value?: Array<string> | string | null;
    name?: string | null;
    operator?: string | null;
    label?: string | null;
  }>,
) => {
  const { formatMessage } = useIntl();
  const client = useQueryClient();
  const { handleError, handleSuccess } = useMessageHandler();

  const { refetch, isFetching, isSuccess, isError, error } = useQuery<
    unknown,
    ErrorType
  >({
    queryKey: [QUERY_KEYS.CANDIDATES_DOWNLOAD],
    queryFn: () => downloadCandidates(filters),
    enabled: false,
  });

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(formatMessage(messages.fileDownloaded));
      client.removeQueries({
        queryKey: [QUERY_KEYS.CANDIDATES_DOWNLOAD],
      });
    }
    if (isError && error) {
      handleError(error);
    }
  }, [isSuccess, isError]);

  return { refetch, isFetching };
};
